import { ChevronsUpDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export interface SortOption<T extends string = string> {
  id: T;
  label: string;
}

export interface SortDropdownProps<T extends string = string> {
  options: SortOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

export function SortDropdown<T extends string = string>({ options, value, onChange }: Readonly<SortDropdownProps<T>>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition bg-surface-primary text-content-tertiary border border-border-primary hover:bg-interactive-hover"
      >
        Sort by
        <ChevronsUpDown className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-44 bg-surface-primary border border-border-primary rounded-xl shadow-lg z-10 py-1 overflow-hidden">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                onChange(option.id);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition ${
                value === option.id
                  ? "text-content-primary bg-interactive-hover"
                  : "text-content-secondary hover:bg-interactive-hover hover:text-content-primary"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
